import * as React from "react";
import {WithApi, WithApiProperties} from "../../common/util/WithApi";
import {defineMessages, FormattedMessage, InjectedIntlProps, injectIntl} from "react-intl";
import {Role} from "../../user/model";
import {ControlRoomTable, ControlRoomTableProperties} from "../../common/ui/table/ControlRoomTable";
import {RolesContentColumnFactory} from "./RolesContentColumnFactory";
import {Logger} from "../../common/util/Logger";
import {NewRoleButton} from "./NewRoleButton";
import {ModalMessageDialog, ModalMessageDialogMode} from "../commons/ModalMessageDialog";
import {ConfirmationDialog} from "../../common/ui/ConfirmationDialog";

const ROLES_DATA_LIST_ROW_HEIGHT = 50;
const MESSAGES = defineMessages({
  deleteRoleSuccess: {
    id: "studio.products.role-management.delete-role-success",
    defaultMessage: "Role deleted successfully."
  },
  deletingRole: {
    id: "studio.products.role-management.deleting-role",
    defaultMessage: "Deleting Role"
  },
  deleteRoleButton: {
    id: "studio.products.role-management.delete-role-button",
    defaultMessage: "Delete Role"
  },
  deleteRoleTitle: {
    id: "studio.products.role-management.delete-role-title",
    defaultMessage: "Delete Role"
  },
  deleteRoleConfirmation: {
    id: "studio.products.role-management.delete-role-confirmation",
    defaultMessage: "Are you sure you want to delete the Role?"
  }
});

interface State {
  roles: Role[];
  selectedIndices: number[];
  modalDialog: JSX.Element[];
  isDeleteDialogOpen: boolean;
  isDeleting: boolean;
  error: Error;
  isSuccess: boolean;
  isExecuting: boolean;
  deleteRole: Role;
}

class RolesPageComponent extends React.Component<WithApiProperties & InjectedIntlProps, State> {
  private RolesListTable: React.ComponentClass<ControlRoomTableProperties<Role>>;
  _logger: Logger = Logger.getLogger("settings.roles-page");

  constructor(props) {
    super(props);
    this.state = {
      roles: [],
      selectedIndices: [],
      modalDialog: [],
      isDeleteDialogOpen: false,
      isDeleting: false,
      error: null,
      isSuccess: false,
      isExecuting: false,
      deleteRole: null,
    }
    this.RolesListTable = ControlRoomTable(
        new RolesContentColumnFactory(
            {
              canEdit: true,
              removeItem: this.removeItem,
            }
        ),
        props.calculateDimensions,
    );
  }

  private closeModal = () => {
    this.setState({...this.state, modalDialog: []})
  }

  private openModal = (modal: JSX.Element) => {
    this.setState({...this.state, modalDialog: [modal]})
  }
  //case-insensitive compare
  ciEquals = (a, b) => {
    return typeof a === 'string' && typeof b === 'string'
           ? a.localeCompare(b, undefined, {sensitivity: 'accent'}) === 0
           : a === b;
  }
  private removeItem = (role: Role) => {
    const {intl} = this.props;
    const roleName = role.role;
    if (this.ciEquals(roleName, "ADMIN") || this.ciEquals(roleName, "STUDIOMANAGER")) {
      this._logger.info("Not allowed to remove " + roleName + " role.");
      return this.openModal(<ModalMessageDialog
          key="remove-role-error"
          onClose={this.closeModal}
          mode={ModalMessageDialogMode.ERROR}
          body={intl.formatMessage({
            id: "studio.products.user-management.admin-role-remove-not-allowed",
            defaultMessage: "Not allowed to remove {name} role."}, {name:roleName})}
      />);
    }
    this.openDeleteDialog(role);
  };

  componentDidMount() {
    this.reloadRoles();
  }

  private reloadRoles() {
    this.props.api.getAllRoles().then((roles: Role[]) => {
          this.setState({...this.state, roles})
        },
        (e) => {
          this._logger.error("Error retrieving roles.", e);
        })
  }

  private createRole = (role: Role) => {
    const {intl} = this.props;
    this.props.api.createRole(role).then(() => {
          this.reloadRoles();
        },
        (e) => {
          this._logger.error("Error occurred while creating a role.", e);
          this.openModal(<ModalMessageDialog
              key="remove-role-error"
              onClose={this.closeModal}
              mode={ModalMessageDialogMode.ERROR}
              body={intl.formatMessage({
                id: "studio.products.role-management.create-role-error",
                defaultMessage: "Failed to create role."
              })}
          />);
        })
  };

  openDeleteDialog = (role: Role) => {
    this.setState({
      ...this.state,
      isExecuting: false,
      error: null,
      isSuccess: false,
      isDeleteDialogOpen: true,
      deleteRole: role
    });

  }

  closeDeleteDialog = () => {
    this.setState({...this.state, isDeleteDialogOpen: false, deleteRole: null});
  }

  performDeletion = () => {
    this.setState({...this.state, isExecuting: true, error: null, isSuccess: false});
    return this.props.api.deleteRole(this.state.deleteRole).then(() => {
      this.reloadRoles();
      this.setState({...this.state, isExecuting: false, error: null, isSuccess: true});
    }).catch((e) => {
      this._logger.error("Error occurred while removing role.", e);
      const error = Error(this.props.intl.formatMessage({
        id: "studio.products.role-management.remove-role-error",
        defaultMessage: "ERROR: Failed to remove role. Make sure the role is not in use."
      }));
      this.setState({...this.state, isExecuting: false, error, isSuccess: false});
    });
  }

  render() {
    const {intl} = this.props;
    const {isDeleteDialogOpen} = this.state;

    return (
        <div>
          <ConfirmationDialog
              show={isDeleteDialogOpen}
              intl={this.props.intl}
              renderConfirmation={() => {
                return (
                    <div>
                      {intl.formatMessage(MESSAGES.deleteRoleConfirmation)} <br/>
                      <strong>{this.state.deleteRole ? this.state.deleteRole.role : ""}</strong>
                    </div>)
              }}
              confirmButtonIcon={"delete"}
              confirmButtonText={intl.formatMessage(MESSAGES.deleteRoleButton)}
              onConfirm={this.performDeletion}
              executing={this.state.isDeleting}
              executingMessage={intl.formatMessage(MESSAGES.deletingRole)}
              error={this.state.error}
              simpleError={true}
              success={this.state.isSuccess}
              warnings={null}
              successMessage={intl.formatMessage(MESSAGES.deleteRoleSuccess)}
              title={intl.formatMessage(MESSAGES.deleteRoleTitle)}
              onHide={this.closeDeleteDialog}
          />
          <h1>
            <FormattedMessage id="studio.users.roles-page.title"
                              defaultMessage="Manage Roles"/>
          </h1>
          <hr/>
          <NewRoleButton onSuccess={this.createRole}/>
          <this.RolesListTable data={this.state.roles}
                               intl={intl}
                               isPaged={false}
                               canMultiSelect={true}
                               isAllDataLoaded={true}
                               selectedRowIndices={this.state.selectedIndices}
                               onRowSelect={() => {
                                 // Not used
                               }}
                               rowHeight={ROLES_DATA_LIST_ROW_HEIGHT}
          />
          {this.state.modalDialog}
        </div>
    );
  }

}

export const RolesPage = injectIntl(WithApi(RolesPageComponent));